import {makeAutoObservable} from 'mobx';
import {CourseObject} from 'api/course';
import {persistence, StorageAdapter} from 'mobx-persist-store';
import {writeStore, readStore} from 'helpers/mobx-persist';
import {ErrorObject, ID, PreviewObject} from 'types';
import axios, {AxiosError, AxiosResponse} from 'axios';

export enum Role {
  Student = 'student',
  Teacher = 'teacher',
}

export type Password = string;

export interface UserObject {
  id: ID;
  username: string;
  email: string;
  name: string;
  avatar: PreviewObject | null;
  videoAvatar?: PreviewObject;
  role: Role;
  favoriteCourseId?: CourseObject['id'];
  contacts?: {
    phone?: string;
    telegram?: string;
    instagram?: string;
    youtube?: string;
  };
}

export type UserState = UserObject | undefined;

/**
 * This class stores user object
 */
export class UserStore {
  private _state: UserState;

  /**
   * Make store observable
   */
  constructor() {
    makeAutoObservable(this);
  }

  /**
   * Get the state
   * @return {UserState}
   */
  get state(): UserState {
    return this._state;
  }

  /**
   * Set the state
   * @param {UserState} state
   */
  set state(state: UserState) {
    this._state = state;
  }

  /**
   * Is the user an student.
   * @return {boolean}
   */
  public isStudent(): boolean {
    return this.state?.role === Role.Student;
  }

  /**
   * Is the user an teacher.
   * @return {boolean}
   */
  public isTeacher(): boolean {
    return this.state?.role === Role.Teacher;
  }

  /**
   * Get the current user's role text.
   * @return {string | undefined}
   */
  public getRoleLabel(): string | undefined {
    switch (this.state?.role) {
      case Role.Student: return 'Студент';
      case Role.Teacher: return 'Преподаватель';
    }
  }

  /**
   * Save user data.
   * @param {UserObject} updatedUser
   * @return {Promise<UserObject | ErrorObject | undefined>}
   */
  public async save(updatedUser: UserObject) {
    this.state = updatedUser;

    return axios({
      method: 'PATCH',
      url: `${process.env.REACT_APP_API_BASE_URL}/user`,
      data: this.state,
    }).then((response: AxiosResponse<UserObject>) => (
      response.data
    )).catch((error: AxiosError<ErrorObject>) => (
      error.response?.data
    ));
  }

  /**
   * Logout the user.
   */
  public logout() {
    this.state = undefined;
  }
}

export default persistence({
  name: 'UserStore',
  properties: ['_state'],
  adapter: new StorageAdapter({
    read: readStore,
    write: writeStore,
  }),
})(new UserStore());

